import * as React from "react";
import {Checkbox} from "@bizform/primereact/checkbox";
import {CheckboxProps} from "@bizform/primereact/components/checkbox/Checkbox";
import TooltipOptions from "@bizform/primereact/components/tooltip/TooltipOptions";
import {DataType} from "@bizform/type/model/model/DataType.model";
import {Static} from "@bizform/type/decorator/Static.decorator";
import {
    BaseStateModel,
    ComponentElement,
    ComponentElementDisplayMode,
    ComponentElementStatic,
    ComponentEventType
} from "@bizform/type/model/component_element/ComponentElement.model";
import {
    ComponentCategory,
    ComponentElementManifest,
    ComponentElementProperty
} from "@bizform/type/model/component_element/ComponentElementManifest.model";
import {ComponentHelper, Debug, ObjectHelper} from "@bizform/helper/dist/js/bizflow/helper.min";
import {ComponentElementClass, ReactComponentElementInit} from "@bizform/core/dist/js/bizflow/core.min";
import {AbstractComponentElement} from "../AbstractComponentElement";
import {I18n} from "../../misc/model/I18n.model";


type Props = CheckboxProps & {
};

type WrappedCompState = {
    // attribute
    id?: string;
    tabindex?: number;
    inputId?: string;
    value?: any;
    name?: string;
    checked?: boolean;
    style?: object;
    printStyle?: object;
    printClassName?: string;
    className?: string;
    disabled?: boolean;
    readOnly?: boolean;
    tooltipAttribute?: TooltipOptions & {text?: string};

    // event
    onChange(e: ComponentEventType): void;
    onMouseDown?(event: Event): void;
    onContextMenu?(event: Event): void;
};

type WrappedFormState = {
    onChange?: string;
};

type State = BaseStateModel & {
    wrappedCompState: WrappedCompState;
    wrappedFormState: WrappedFormState;
};

/**
 * Checkbox component element
 *
 * @since 0.0.1
 * @author Jongkwang Park
 * @class
 */
@ComponentElementClass()
@Static<ComponentElementStatic<boolean>>()
export class BfCheckbox extends AbstractComponentElement<Props, State> {
    /* manifest */
    static readonly componentElementManifest: ComponentElementManifest = {
        uniqueId: "_MQySm_hLLTRDrdAp3CtuhHzcb4o6pXx",
        category: ComponentCategory.INPUT,
        label: I18n.componentElement.checkbox.label,
        description: I18n.componentElement.checkbox.description,
        icon: "",
        dataType: [DataType.BOOLEAN],
        property: null
    };

    focus(): void {
        if (this._checkbox) {
            this._checkbox.querySelector("input").focus();
        }
    }

    setProperty(property: ComponentElementProperty): void {
        try {
            const newState = ComponentHelper.createStateWithProperties<State, ComponentEventType>(
                property,
                this);
            this.setState(newState);
        } catch (e) {
            this.emitDidCatch(e);
        }
    }

    emit(value: boolean): void {
    }

    emitFormState(value: string): void {
    }

    subscription(value: boolean): void {
        if (ComponentHelper.isNull(value)) {
            const newState = {
                wrappedCompState: {
                    ...this.state.wrappedCompState,
                    checked: false
                }
            };
            this.setState(newState);
        } else if ( typeof value === "boolean") {
            const newState = {
                wrappedCompState: {
                    ...this.state.wrappedCompState,
                    checked: value
                }
            };
            this.setState(newState);
        } else if (typeof value === "number" && ( value === 0 || value === 1)) {
            const newState = {
                wrappedCompState: {
                    ...this.state.wrappedCompState,
                    checked: value ? true : false
                }
            };
            this.setState(newState);
        } else {
            this.emitSubscriptionReject();
        }
    }

    @ReactComponentElementInit(BfCheckbox)
    static init(): ComponentElement<boolean> {
        return;
    }

    initDone(): void {
    }

    destroy(): void {
        this.destroyDone();
    }

    destroyDone(): void {
    }

    /* for React */
    props: Props;
    state: State;

    private _checkbox: HTMLInputElement;

    constructor(props?: Props) {
        super(props);

        this.state = {
            show: false,
            displayMode: ComponentElementDisplayMode.INIT,
            wrappedCompState: {
                onChange: null
            },
            wrappedFormState: {}
        };

        this._onChangeEvent = this._onChangeEvent.bind(this);
        this._setRef = this._setRef.bind(this);
    }

    shouldComponentUpdate(nextProps: Readonly<Props>, nextState: Readonly<State>, nextContext: any): boolean {
        return ObjectHelper.hasDifference(nextState, this.state);
    }

    componentWillUnmount(): void {
        this._checkbox = null;
    }

    componentDidUpdate(): void {
        if (this._checkbox) {
            this._makeStyle();
            if (this.state.wrappedCompState.tabindex !== this.___tabindex) {
                ComponentHelper.tabindex(this.displayMode, this._checkbox.querySelector("input"), this.state.wrappedCompState.tabindex);
                this.___tabindex = this.state.wrappedCompState.tabindex;
            }
        }
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        this.emitDidCatch(error);
    }

    emitDidCatch(error: Error | any): void {
        Debug.console.error(error);
    }

    emitSubscriptionReject(): void {
    }

    private _onChangeEvent(e: ComponentEventType): void {
        const newState = {
          wrappedCompState: {
              ...this.state.wrappedCompState,
              checked: e.checked
          }
        };

        this.setState(newState, this._callbackSetState);
    }

    private _callbackSetState() {
        this.emit(this.state.wrappedCompState.checked);
    }

    render(): React.ReactNode {
        const {
            show,
            displayMode
        } = this.state;

        if (!show) {
            return null;
        } else {
            switch (displayMode) {
                case ComponentElementDisplayMode.EDIT:
                    return this._render4Edit();
                case ComponentElementDisplayMode.READONLY:
                    return this._render4ReadOnly();
                case ComponentElementDisplayMode.DISABLED:
                    return this._render4Disabled();
                case ComponentElementDisplayMode.PRINT:
                    return this._render4Print();
                default:
                    return null;
            }
        }
    }

    private _render(newProps: Props): React.ReactNode {
        const key = ComponentHelper.tooltipProps(newProps, this.elementId, this.accessibility);
        newProps.style = null;
        delete(newProps["printClassName"]);
        delete(newProps["printStyle"]);
        return (
            <Checkbox
                inputId={this.elementId}
                key={key}
                ref={this._setRef}
                {...newProps}
                style={null}
            />
        );
    }

    private _render4Edit(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState
        };

        return this._render(newProps);
    }

    private _render4ReadOnly(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState,
            readOnly: true
        };

        return this._render(newProps);
    }

    private _render4Disabled(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState,
            disabled: true
        };

        return this._render(newProps);
    }

    private _render4Print(): React.ReactNode {
        return (
            <div ref={this._setRef} style={this.state.wrappedCompState ? {...this.state.wrappedCompState.printStyle} : null} className= {`bf-print-mode ${this.state.wrappedCompState ? this.state.wrappedCompState.printClassName : null}`}>{this.state.wrappedCompState.checked ? "true" : "false"}</div>
        );
    }

    private _setRef(ref): void {
        if (ref) {
            this._checkbox = ref.element;
            this._makeStyle();
            this.readyComponent();
        }
    }

    /**
     * @author Chris Park
     * @param newProps
     * 프라임 리액트에서 잘못 지정해논 컴포넌트 클래스와 스타일을 재 지정하는 함수다
     * div 하위 객체 두번째 꺼에 스타일과 클래스가 걸려야 원하는 모양이 출력된다
     * 먼저 걸어야 할 위치를 받아오고 값을 초기값으로 리셋한다(제거된 스타일 누적 방지)
     * 앱툴에서 걸어준 스타일 값을(newprops) 받아와 이를 위에 설정한 태그에 걸어준다
     * 실제 렌더하는 부분에서 기존 스타일 값을 리셋시켜주어 중복 스타일을 방지한다
     */
    private _makeStyle(newProps = {...this.state.wrappedCompState}): void {
        if (this.state.displayMode === ComponentElementDisplayMode.EDIT || this.state.displayMode === ComponentElementDisplayMode.READONLY || this.state.displayMode === ComponentElementDisplayMode.DISABLED) {
            if (this._checkbox) {
                let span = this._checkbox.querySelectorAll("div")[1];
                span.setAttribute("style", "");
                for (let item in newProps.style) {
                    (span as any).style[item] = (newProps as any).style[item];
                }
            }
        }
     }
}
